import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AppTopBar, formatIDR } from "@/components/AppTopBar";
import { useAuth } from "@/context/AuthContext";

const n = (v) => parseFloat(v || 0) || 0;

function SalesChart({ rows }) {
  const data = (rows || []).map((r) => ({ ...r, value: n(r.sales) }));
  const max = Math.max(1, ...data.map((r) => r.value));
  const w = 1000, h = 170, px = 30, py = 16, bottom = 28;
  const innerW = w - px * 2;
  const innerH = h - py - bottom;
  const pts = data.map((r, i) => {
    const x = px + (data.length <= 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
    const y = py + innerH - (r.value / max) * innerH;
    return { ...r, x, y };
  });
  const path = pts.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const labelIdx = new Set([0, Math.floor((data.length - 1) / 2), data.length - 1]);
  const fmtDate = (s) => {
    if (!s) return "";
    const d = new Date(`${s}T00:00:00`);
    return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short" });
  };

  return (
    <div style={{ width: "100%", overflow: "hidden" }}>
      <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Grafik penjualan harian" style={{ width: "100%", height: 180, display: "block" }}>
        {[0, .25, .5, .75, 1].map((t) => {
          const y = py + innerH - t * innerH;
          return <g key={t}><line x1={px} y1={y} x2={w-px} y2={y} stroke="currentColor" opacity="0.08" /><text x={px} y={y-4} fontSize="10" opacity="0.52">{formatIDR(max*t)}</text></g>;
        })}
        {path && <path d={path} fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.82" />}
        {pts.map((p, i) => <g key={p.date}><circle cx={p.x} cy={p.y} r="3" fill="currentColor"><title>{fmtDate(p.date)} · {formatIDR(p.value)}</title></circle>{labelIdx.has(i) && <text x={p.x} y={h-7} textAnchor={i===0?"start":i===data.length-1?"end":"middle"} fontSize="10" opacity="0.58">{fmtDate(p.date)}</text>}</g>)}
      </svg>
    </div>
  );
}

export default function ErpDashboard() {
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [lowStock, setLowStock] = useState({ count: 0, items: [] });
  const [health, setHealth] = useState(null);
  const [skuHealth, setSkuHealth] = useState({ items: [], count: 0, status_counts: {} });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    setError("");

    const load = async () => {
      const errors = [];

      const [d, low, skuHealthResult, h] = await Promise.allSettled([
        api.get(`/erp-dashboard?days=${days}&top_n=8`),
        api.get("/performance/dashboard-low-stock?limit=5"),
        api.get("/analytics/sku-health"),
        api.get("/reports/dashboard"),
      ]);

      if (cancelled) return;

      if (d.status === "fulfilled") {
        setData(d.value.data);
      } else {
        errors.push(formatApiErrorDetail(d.reason?.response?.data?.detail) || "Ringkasan Dashboard ERP gagal dimuat.");
      }

      if (low.status === "fulfilled") {
        setLowStock(low.value.data || { count: 0, items: [] });
      } else {
        errors.push("Data stok menipis gagal dimuat.");
      }

      if (skuHealthResult.status === "fulfilled") {
        setSkuHealth(skuHealthResult.value.data || { items: [], count: 0, status_counts: {} });
      } else {
        errors.push("Kesehatan SKU gagal dimuat.");
      }

      if (h.status === "fulfilled") {
        setHealth(h.value.data || null);
      } else {
        errors.push("Health Check gagal dimuat.");
      }

      setError(errors.join(" "));
    };

    load();
    return () => { cancelled = true; };
  }, [days, isOwner]);

  if (user && !isOwner) return (<div className="app-shell"><AppTopBar /><main className="content"><div className="alert-error">Akses ditolak.</div></main></div>);

  const card = { background: "#fff", border: "1px solid var(--border)", borderRadius: 10, padding: "11px 12px", minHeight: 76, textDecoration: "none", color: "inherit", boxSizing: "border-box", minWidth: 0 };
  const compactCell = { paddingTop: 7, paddingBottom: 7 };
  const panelStyle = { margin: 0, padding: 14, minWidth: 0, width: "100%", maxWidth: "100%", boxSizing: "border-box", overflow: "hidden" };
  const lowStockItems = lowStock?.items || [];

  return (
    <div className="app-shell">
      <AppTopBar active="erp-dashboard" />
      <main className="content" style={{ paddingTop: 18, overflowX: "hidden" }}>
        <div style={{ width: "100%", maxWidth: 1080, margin: "0 auto", boxSizing: "border-box" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", marginBottom: 10, flexWrap: "wrap" }}>
            <div><h1 style={{ margin: "0 0 4px", fontSize: 24 }}>Dashboard ERP</h1><p className="hint" style={{ margin: 0 }}>Ringkasan Owner: penjualan, uang, kewajiban, stok, dan masalah.</p></div>
            <div><label className="field-label">PERIODE</label><select className="field-input" value={days} onChange={(e)=>setDays(parseInt(e.target.value,10))}><option value={7}>7 hari</option><option value={30}>30 hari</option><option value={60}>60 hari</option><option value={90}>90 hari</option></select></div>
          </div>

          {error && <div className="alert-error">{error}</div>}

          <section className="panel" style={{ ...panelStyle, marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}><div><h2 className="panel-title" style={{ marginBottom: 2 }}>Penjualan Harian</h2><span className="hint">Gross omzet marketplace berdasarkan Tanggal Pembuatan order (tanpa order cancel/refund atau tanpa tanggal) + Manual Sales POSTED.</span></div><b data-testid="dash-total-sales">{data ? formatIDR(data.total_sales) : "-"}</b></div>
            <SalesChart rows={data?.daily_sales || []} />
            {data && n(data.excluded_cancel_refund_order_count) > 0 && (
              <div className="hint" data-testid="dash-excluded" style={{ marginTop: 4 }}>Dikecualikan (cancel/refund): {data.excluded_cancel_refund_order_count} order · {formatIDR(data.excluded_cancel_refund_gross)}</div>
            )}
            {data && n(data.still_undated_order_count) > 0 && (
              <div className="hint" data-testid="dash-undated" style={{ marginTop: 2 }}>Tanpa tanggal marketplace: {data.still_undated_order_count} order · {formatIDR(data.still_undated_gross)}</div>
            )}
          </section>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 8, marginBottom: 8 }}>
            <div style={card}><div className="meta-label">TOTAL PENJUALAN</div><div className="mono" style={{ fontSize: 19, fontWeight: 700, marginTop: 5 }}>{data ? formatIDR(data.total_sales) : "-"}</div><div className="hint">Periode terpilih</div></div>
            <Link to="/finance/settlement" style={card}><div className="meta-label">TOTAL PELUNASAN</div><div className="mono" style={{ fontSize: 19, fontWeight: 700, marginTop: 5 }}>{data ? formatIDR(data.total_settlement) : "-"}</div><div className="hint">Settlement NET periode terpilih →</div></Link>
            <Link to="/finance/tanam-piutang" style={card}><div className="meta-label">SISA PIUTANG</div><div className="mono" style={{ fontSize: 19, fontWeight: 700, marginTop: 5 }}>{data ? formatIDR(data.receivable_open) : "-"}</div><div className="hint">OPEN + UNSETTLED saat ini →</div></Link>
            <Link to="/purchases" style={card}><div className="meta-label">HUTANG SUPPLIER</div><div className="mono" style={{ fontSize: 19, fontWeight: 700, marginTop: 5 }}>{data ? formatIDR(data.supplier_debt) : "-"}</div><div className="hint">Purchase non-DRAFT - pembayaran POSTED →</div></Link>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 8, marginBottom: 8 }}>
            <Link to="/inventory" style={{ ...card, minHeight: 78 }}><div className="meta-label">SKU MENIPIS / HABIS</div><div style={{ display: "flex", alignItems: "baseline", gap: 7, marginTop: 4 }}><b style={{ fontSize: 24 }}>{lowStock?.count ?? 0}</b><span className="hint">SKU perlu dicek</span></div><div className="hint">Distribusi ≤ buffer / 1 pack →</div></Link>
            <Link to="/reports" style={{ ...card, minHeight: 78, borderColor: n(health?.fund_mismatch_count) > 0 ? "#d97706" : "var(--border)" }}><div className="meta-label">DANA BERMASALAH</div><div style={{ display: "flex", alignItems: "baseline", gap: 7, marginTop: 4 }}><b style={{ fontSize: 24 }}>{health?.fund_mismatch_count ?? "-"}</b><span className="hint">Fund mismatch</span></div><div className="hint">{n(health?.fund_mismatch_count)>0 ? "Perlu tindakan" : "Aman"} · Health Check →</div></Link>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 10, width: "100%", overflow: "hidden" }}>
            <section className="panel" style={panelStyle}>
              <div className="panel-head" style={{ marginBottom: 6 }}><h2 className="panel-title" style={{ margin: 0 }}>SKU Perlu Dicek</h2><Link to="/inventory" className="btn-mini">Buka Inventory</Link></div>
              <div style={{ width: "100%", overflowX: "auto" }}><table className="data-table" style={{ width: "100%", tableLayout: "fixed" }}><thead><tr><th style={{ ...compactCell, width: "24%" }}>SKU</th><th style={{ ...compactCell, width: "52%" }}>Produk</th><th style={{ ...compactCell, width: "24%" }}>Distribusi</th></tr></thead><tbody>
                {lowStockItems.length === 0 && <tr><td colSpan={3} className="empty-cell">Tidak ada SKU menipis.</td></tr>}
                {lowStockItems.map((it)=><tr key={it.product_id}><td className="mono" style={compactCell}>{it.sku_code}</td><td style={{ ...compactCell, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.description || it.product_name || "-"}</td><td className="mono" style={compactCell}>{it.distribution_available ?? 0}</td></tr>)}
              </tbody></table></div>
            </section>

            <section className="panel" style={panelStyle}>
              <div className="panel-head" style={{ marginBottom: 6 }}><div><h2 className="panel-title" style={{ marginBottom: 2 }}>Produk Penjualan Tertinggi</h2><div className="hint">Ranking qty periode terpilih.</div></div></div>
              <div style={{ width: "100%", overflowX: "auto" }}><table className="data-table" style={{ width: "100%", tableLayout: "fixed" }}><thead><tr><th style={{ ...compactCell, width: "10%" }}>#</th><th style={{ ...compactCell, width: "24%" }}>SKU</th><th style={{ ...compactCell, width: "46%" }}>Produk</th><th style={{ ...compactCell, width: "20%" }}>Qty</th></tr></thead><tbody>
                {(!data?.top_products || data.top_products.length===0) && <tr><td colSpan={4} className="empty-cell">Belum ada data penjualan.</td></tr>}
                {(data?.top_products || []).slice(0,5).map((p,i)=><tr key={`${p.sku}-${i}`}><td style={compactCell}>{i+1}</td><td className="mono" style={compactCell}>{p.sku}</td><td style={{ ...compactCell, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.product || "-"}</td><td className="mono" style={compactCell}><b>{p.qty}</b></td></tr>)}
              </tbody></table></div>
            </section>
          </div>

          <section className="panel" style={{ ...panelStyle, marginTop: 0, marginBottom: 10 }}>
            <div className="panel-head" style={{ marginBottom: 8 }}>
              <div><h2 className="panel-title" style={{ margin: 0 }}>Kesehatan SKU</h2><div className="hint">Seluruh SKU ACTIVE berdasarkan penjualan berjalan.</div></div>
              <Link to="/analytics/deadstock" className="btn-mini">Buka Deadstock</Link>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5,minmax(0,1fr))", gap: 7 }}>
              {[
                ["NO SALE", "NO_SALE", "#c21f2d"],
                ["STAGNANT", "STAGNANT", "#b45309"],
                ["SLOWDOWN", "SLOWDOWN", "#a16207"],
                ["ACTIVE", "ACTIVE", "#2563eb"],
                ["ACCELERATING", "ACCELERATING", "#15803d"],
              ].map(([label, key, color]) => (
                <div key={key} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "8px 10px", minWidth: 0 }}>
                  <div className="meta-label">{label}</div>
                  <b style={{ display: "block", marginTop: 3, fontSize: 20, color }}>{Number(skuHealth?.status_counts?.[key] || 0).toLocaleString("id-ID")}</b>
                </div>
              ))}
            </div>
            <div className="hint" style={{ marginTop: 8 }}>Prioritas: NO SALE → STAGNANT → SLOWDOWN. Nilai aset tetap menjadi dasar keputusan pelepasan stok.</div>
          </section>

        </div>
      </main>
    </div>
  );
}
